import { BuyerUI } from '@choco-admin/types';
import React, { useCallback, useMemo } from 'react';

import { useNotification } from '../../../../contexts/NotificationContext';
import { useUpdateBuyer } from '../../hooks';
import { buildBuyerUpdateInput, getUpdateBuyerFailureCopy } from '../../utils';
import { BuyerForm } from '../BuyerForm';
import type { BuyerFormData } from '../BuyerForm';

import { LocationDataPanel } from './LocationDataPanel';

interface ProfileTabProps {
  buyer: BuyerUI;
}

const isValidAddressSource = (
  source: string | null | undefined
): source is 'custom' | 'google' => {
  return source === 'custom' || source === 'google';
};

export const ProfileTab: React.FC<ProfileTabProps> = ({ buyer }) => {
  const { updateBuyer } = useUpdateBuyer({});
  const { addNotification } = useNotification();

  const initialData = useMemo(
    () => ({
      name: buyer.name || '',
      displayName: buyer.displayName || '',
      fullAddress: buyer.fullAddress || '',
      deliveryComment: buyer.deliveryComment || '',
      addressCity: buyer.city || '',
      addressCountry: buyer.country || '',
      isTest: buyer.isTest || false,
      // `??`, not `||`: these seed the save payload, and 0 is a legal value for
      // each of them.
      addressLatitude: buyer.latitude ?? undefined,
      addressLongitude: buyer.longitude ?? undefined,
      addressUTCOffset: buyer.utcOffset ?? undefined,
      addressSource: isValidAddressSource(buyer.addressSource)
        ? buyer.addressSource
        : undefined,
      // buyerUpdate rejects addressSource 'google' unless googlePlaceId,
      // latitude, and longitude are present — seed them from the loaded buyer
      // so re-saving an untouched google address round-trips.
      addressGooglePlaceId: buyer.googlePlaceId || undefined,
      addressGoogleAddressId: buyer.googlePlaceAddressId || undefined,
    }),
    [buyer]
  );

  const handleSubmit = useCallback(
    async (data: BuyerFormData) => {
      // Only send address keys when an address field actually changed. On an
      // unrelated edit the backend would otherwise re-derive the address from
      // the stored (possibly retired) Google place on every save
      // (choco-backend updateBuyer.ts:72-77) — a live round-trip that reverts
      // manual city/country edits and 500s on dead place ids.
      const input = buildBuyerUpdateInput(buyer.id, data, initialData);

      try {
        await updateBuyer(input);
        addNotification({
          title: 'Success',
          message: 'Buyer updated successfully',
          type: 'success',
        });

        // What the backend now holds, which is not always what was submitted:
        // a google-to-custom switch is saved with both place ids cleared. The
        // form rebaselines on this, so returning the submitted values instead
        // would leave the cleared ids in the form and resend them next save.
        const sentAddress = input.addressSource !== undefined;
        return sentAddress
          ? {
              ...data,
              addressGooglePlaceId: input.addressGooglePlaceId ?? undefined,
              addressGoogleAddressId: input.addressGoogleAddressId ?? undefined,
            }
          : data;
      } catch (error) {
        // The backend's own text is written for engineers ("Something went
        // wrong", "Bad Request"), so it never reaches the screen. Whether the
        // save carried a Google address decides whether switching to a custom
        // address is a way past the failure.
        const { title, message } = getUpdateBuyerFailureCopy({
          errorMessage: error instanceof Error ? error.message : '',
          sentGoogleAddress: input.addressSource === 'google',
        });

        addNotification({ title, message, type: 'error' });
        return undefined;
      }
    },
    [buyer.id, updateBuyer, addNotification, initialData]
  );

  return (
    <div className="w-full max-w-4xl mx-auto">
      <BuyerForm
        mode="edit"
        initialData={initialData}
        onSubmit={handleSubmit}
        title="Buyer Information"
      />
      {/* Below the form: the buyer's details are what CS came for, and this is
          reference data rather than something to act on. */}
      <LocationDataPanel buyer={buyer} />
    </div>
  );
};
